import React, { useState, useRef, useCallback, useEffect } from 'react';
import { colors } from '../styles/theme';
import './PhotoPositioner.css';

function PhotoPositioner({
  photoUrl,
  positionX = 50,
  positionY = 50,
  zoom: initialZoom = 100,
  onSave,
  onCancel,
  aspectRatio,
}) {
  const [x, setX] = useState(positionX);
  const [y, setY] = useState(positionY);
  const [zoom, setZoom] = useState(initialZoom);
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef(null);
  const startRef = useRef({ mouseX: 0, mouseY: 0, startX: 0, startY: 0 });

  const handlePointerDown = useCallback(
    (e) => {
      e.preventDefault();
      setDragging(true);
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      startRef.current = { mouseX: clientX, mouseY: clientY, startX: x, startY: y };
    },
    [x, y]
  );

  const handlePointerMove = useCallback(
    (e) => {
      if (!dragging || !containerRef.current) return;
      e.preventDefault();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const rect = containerRef.current.getBoundingClientRect();

      const deltaX = ((clientX - startRef.current.mouseX) / rect.width) * 100;
      const deltaY = ((clientY - startRef.current.mouseY) / rect.height) * 100;

      const newX = Math.round(Math.min(100, Math.max(0, startRef.current.startX - deltaX)));
      const newY = Math.round(Math.min(100, Math.max(0, startRef.current.startY - deltaY)));

      setX(newX);
      setY(newY);
    },
    [dragging]
  );

  const handlePointerUp = useCallback(() => {
    setDragging(false);
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const moveHandler = (e) => handlePointerMove(e);
    const upHandler = () => handlePointerUp();
    window.addEventListener('mousemove', moveHandler);
    window.addEventListener('mouseup', upHandler);
    window.addEventListener('touchmove', moveHandler, { passive: false });
    window.addEventListener('touchend', upHandler);
    return () => {
      window.removeEventListener('mousemove', moveHandler);
      window.removeEventListener('mouseup', upHandler);
      window.removeEventListener('touchmove', moveHandler);
      window.removeEventListener('touchend', upHandler);
    };
  }, [dragging, handlePointerMove, handlePointerUp]);

  const scale = zoom / 100;

  return (
    <div className="photo-positioner">
      <div className="photo-positioner-label">Drag to reposition</div>
      <div
        ref={containerRef}
        className={`photo-positioner-preview${dragging ? ' dragging' : ''}`}
        style={aspectRatio ? { aspectRatio: String(aspectRatio) } : undefined}
        onMouseDown={handlePointerDown}
        onTouchStart={handlePointerDown}
      >
        <img
          src={photoUrl}
          alt="Reposition preview"
          draggable={false}
          style={{
            objectPosition: `${x}% ${y}%`,
            transform: scale !== 1 ? `scale(${scale})` : undefined,
            transformOrigin: `${x}% ${y}%`,
          }}
        />
        <div className="photo-positioner-crosshair">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="rgba(255,255,255,0.8)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="12" y1="2" x2="12" y2="22" />
            <line x1="2" y1="12" x2="22" y2="12" />
          </svg>
        </div>
      </div>
      <div
        className="photo-positioner-zoom"
        style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px' }}
      >
        {/* REVIEW: theme tokens */}
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontFamily: 'Inter, sans-serif',
            fontSize: '12px',
            fontWeight: 600,
            color: colors.textSecondary,
            whiteSpace: 'nowrap',
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
            <line x1="11" y1="8" x2="11" y2="14" />
            <line x1="8" y1="11" x2="14" y2="11" />
          </svg>
          Zoom
        </label>
        <input
          type="range"
          min="100"
          max="200"
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
          style={{ flex: 1 }}
        />
        {/* REVIEW: theme tokens */}
        <span
          style={{
            fontFamily: 'Inter, monospace',
            fontSize: '12px',
            fontWeight: 600,
            color: colors.textSecondary,
            minWidth: '36px',
            textAlign: 'right',
          }}
        >
          {zoom}%
        </span>
      </div>
      <div className="photo-positioner-actions">
        <button className="photo-positioner-save" onClick={() => onSave(x, y, zoom)}>
          Save
        </button>
        <button className="photo-positioner-cancel" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export default PhotoPositioner;
